import { RECORD_TYPE } from "../../../../const/constants";
import { MESSAGES } from "../../../../common/Messages";
import { GetFormatedDateWithTime } from "../../../../common/Utils";

/**
 * Validation rules of the Delphi Rate Schedule screen (uWMM0017 BeforePost handlers), shared by the
 * header form and the Charges tab grids. RateScheduleHeaderService / RateScheduleDateService /
 * RateScheduleDetailService enforce the same blocking rules server-side, with the codes in
 * Argos.Core/Constants/RateScheduleConstants.
 */

/** WMRATESCHEDULEHEADER.SCHEDULETYPE codes. */
export const SCHEDULE_TYPES = {
    ITEM: 'I',
    TRANSACTION: 'T',
    STORAGE: 'S',
};

/** WMRATESCHEDULEDETAIL.USAGE codes that bill a third party (Delphi tests 'T' or 'B'). */
export const BILL_THIRD_PARTY_USAGES = ['T', 'B'];

/** WMRATESCHEDULEDETAIL.RATESOURCE: take the rate from the rate schedule detail itself. */
export const RATE_SOURCE_RATE_SCHEDULE = 'R';

// A header not yet saved has an empty RECORDTYPE and becomes 'R' on insert, so "not a profile
// template" is what Delphi's RecordType = 'R' means here.
const isRealRecord = (recordType) => recordType !== RECORD_TYPE.PROFILE_DEFAULTS;

const isBlank = (value) => value === null || value === undefined || value === '';

/**
 * qryWMRateScheduleHeaderBeforePost: a real Storage Transaction Schedule must name its customer.
 * Returns the error message, or null.
 */
export const getHeaderError = (formFields) => (
    isRealRecord(formFields.RECORDTYPE)
        && formFields.SCHEDULETYPE === SCHEDULE_TYPES.STORAGE
        && isBlank(formFields.R_CUSTOMER)
        ? MESSAGES.RATE_SCHEDULE_CUSTOMER_REQUIRED
        : null
);

/**
 * When qryWMRateScheduleDetailBeforePost runs its checks: (RecordType = 'R') and (SCHEDULETYPE = 'T')
 * or (SCHEDULETYPE = 'I'). Pascal binds "and" tighter than "or", so an Item schedule is checked
 * whatever its record type, a Transaction schedule only when real.
 */
export const detailChecksApply = (recordType, scheduleType) => (
    (isRealRecord(recordType) && scheduleType === SCHEDULE_TYPES.TRANSACTION)
    || scheduleType === SCHEDULE_TYPES.ITEM
);

/** The two blocking detail checks. Returns the error message, or null. */
export const getDetailRowError = (row, recordType, scheduleType) => {
    if (!detailChecksApply(recordType, scheduleType)) {
        return null;
    }

    if (isBlank(row.RChargeCode)) {
        return MESSAGES.RATE_SCHEDULE_CHARGE_CODE_REQUIRED;
    }

    if (BILL_THIRD_PARTY_USAGES.includes(row.Usage) && isBlank(row.RBillTo)) {
        return MESSAGES.RATE_SCHEDULE_BILL_TO_REQUIRED;
    }

    return null;
};

/** The detail warning: Rate Source says "use this row's rate" but the rate is empty (yes/no). */
export const isEmptyRateWarning = (row, recordType, scheduleType) => (
    detailChecksApply(recordType, scheduleType)
    && row.RateSource === RATE_SOURCE_RATE_SCHEDULE
    && isBlank(row.Rate)
);

/**
 * uWMM0017.qryWMRateScheduleDetailAfterInsert: a new charge line starts as Type 'R' (Receipt),
 * Usage 'Y' (Always Use) and Rate Source 'C' (Charge Code).
 */
export const NEW_DETAIL_DEFAULTS = {
    Type: 'R',
    Usage: 'Y',
    RateSource: 'C',
};

// A new line's SequenceNo is assigned by RateScheduleDetailService (the date's highest + 1, as Delphi
// numbered it): the grid only sees the rows its filter row leaves visible, so it cannot do it reliably.

/**
 * uWMM0017.qryWMRateScheduleHeaderAfterInsert: a new header starts as a Transaction Schedule, applied
 * automatically, active, with lot/sublot storage end dates off and recurring storage on. (Its
 * STORAGEFREQUENCY 'W' has no control here; RateScheduleHeaderService stamps it.)
 */
export const NEW_HEADER_DEFAULTS = {
    SCHEDULETYPE: SCHEDULE_TYPES.TRANSACTION,
    SCHEDULEAPPLY: 'A',
    STATUS: 'A',
    LOTSTORAGEENDDATE: 'N',
    SUBLOTSTORAGEENDDATE: 'N',
    RECURRINGSTORAGE: 'Y',
};

/**
 * uWMM0017.qryWMRateScheduleDateAfterInsert: a new date row's Effective Date is today. Returned as a
 * local, zone-less "yyyy-MM-ddT00:00:00" string (the column's LOCAL_DATE_TIME_SERIALIZATION_FORMAT)
 * rather than a Date, so an untouched default reaches the API as today - a Date would be sent as UTC.
 */
export const newDateEffectiveDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return GetFormatedDateWithTime(today);
};
